import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import {
  ArrowRight,
  CircleDot,
  Compass,
  FileText,
  LockKeyhole,
  Orbit,
  Sparkles,
  Users,
  ShieldAlert,
  Zap,
  Activity,
  Award,
  Check,
  Globe,
  Flame,
  Calendar,
  Layers,
  Printer,
  Sliders,
  DollarSign,
  TrendingUp,
  Share2,
  Brain,
} from "lucide-react";
import { apiUrl } from "../utils/api";
import { ReceiptsSection } from "./ReceiptsSection_Components/ReceiptsSection";
import { CapabilitiesSection } from "./CapabilitiesSection_Components/CapabilitiesSection";
import { CommonsSection } from "./CommonsSection_Components/CommonsSection";
import { DataProtectionSection } from "./DataProtectionSection_Components/DataProtectionSection";
import { NexIdentitySection } from "./NexIdentitySection_Components/NexIdentitySection";
import { FinalLandingSection } from "./FinalLandingSection_Components/FinalLandingSection";
import { LandingGuidePanel } from "./LandingGuidePanel_Components/LandingGuidePanel";

interface Placement {
  planet: string;
  longitude: number;
  zodiac_sign: string;
  degree_in_sign: string;
  house: number;
  retrograde: boolean;
}

interface NatalAspect {
  source: string;
  target: string;
  aspect_type: string;
  angle: number;
  orb: number;
  color: string;
}

interface ChartAngles {
  ascendant: { degree_in_sign: string } | null;
  midheaven: { degree_in_sign: string } | null;
  cusps: number[];
  house_system: string;
  available: boolean;
}

interface TransitInsight {
  source: string;
  target: string;
  aspect_type: string;
  orb: number;
  color: string;
  priority: string;
  meaning: string;
  headline: string;
}

interface TransitReport {
  target_date: string;
  aspects: TransitInsight[];
  summary: string;
  disclaimer: string;
}

interface NatalChart {
  birth_date: string;
  birth_time: string | null;
  birth_time_used: boolean;
  birth_city: string;
  location: {
    latitude: number;
    longitude: number;
    timezone_name: string;
    resolved_location: string;
  };
  angles: ChartAngles;
  location_precision: string;
  placements: Placement[];
  aspects: NatalAspect[];
  save_note: string;
}

interface TelemetryStripData {
  frictionScore: number;
  aspectCount: number;
  tightestAspect: string;
  tightestOrb: string;
  tacticalDirective: string;
}

const ZODIAC_SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer",
  "Leo", "Virgo", "Libra", "Scorpio",
  "Sagittarius", "Capricorn", "Aquarius", "Pisces"
];

const PRICING_TIERS = [
  {
    name: "Explorer",
    price: "$0",
    cadence: "forever",
    desc: "Instant top-of-funnel entry point. Free 2D birth chart generation and baseline 3D observatory preview.",
    features: [
      "Interactive 2D topocentric chart generator",
      "Full 24-body placement & aspect table",
      "Printable high-DPI vector chart wheel",
      "Real-time global Cosmic Friction preview",
    ],
    cta: "Generate Free Chart",
    highlight: false,
    badge: null,
  },
  {
    name: "Standard Observer",
    price: "$9",
    cadence: "/ month",
    desc: "Personal astronomical tracking for self-directed operators replacing consumer horoscope toys.",
    features: [
      "Full spatial observatory access",
      "Precise planetary placement calculations",
      "Daily personal transit pressure alerts",
      "Multi-Zodiac frames (Tropical, Sidereal)",
      "500 Solar Fuel monthly protocol units",
    ],
    cta: "Activate Observer",
    highlight: false,
    badge: null,
  },
  {
    name: "Quantitative Operator",
    price: "$17",
    cadence: "/ month",
    desc: "Temporal navigation and multi-party persona projection for founders and operators.",
    features: [
      "All Standard features included",
      "Astro-Chronos 4D temporal time scrubber",
      "Google Calendar schedule transit intersect",
      "Up to 5 Private Persona Profiles",
      "1,200 Solar Fuel monthly protocol units",
    ],
    cta: "Deploy Operator",
    highlight: false,
    badge: "Popular",
  },
  {
    name: "Tactical Strategist",
    price: "$36",
    cadence: "/ month",
    desc: "Advanced decision support for resolving competing pressures and choosing clear next steps.",
    features: [
      "All Operator features included",
      "Advanced Eye in the Sky tactical readings",
      "Daily imperative tactical containment rules",
      "Draconic karmic plane coordinate projection",
      "Up to 15 Private Persona Profiles",
      "3,000 Solar Fuel monthly protocol units",
    ],
    cta: "Engage Strategist",
    highlight: true,
    badge: "Pro choice",
  },
  {
    name: "Executive Advisory",
    price: "$71",
    cadence: "/ month",
    desc: "Custom decision frameworks and multi-entity analysis for venture partners.",
    features: [
      "Custom decision framework creation",
      "Publish to Astral Commons and earn 50% royalties",
      "Corporate deal and term-sheet entity projection",
      "40% affiliate referral commission access",
      "7500 Solar Fuel monthly protocol units",
    ],
    cta: "Access Advisory",
    highlight: false,
    badge: "Institutional",
  },
  {
    name: "Enterprise Leadership",
    price: "$141",
    cadence: "/ month",
    desc: "Unlimited access for executive recruiters, investment funds, and portfolio leadership.",
    features: [
      "Unlimited Private Persona & Deal Entities",
      "Boardroom-grade executive due-diligence reviews",
      "Priority access for enterprise workloads",
      "Direct API access & custom webhook endpoints",
      "2,000 Solar Fuel monthly protocol units",
    ],
    cta: "Commission Enterprise",
    highlight: false,
    badge: "Max capacity",
  },
];

export const ChartWheel: React.FC<{ placements: Placement[]; aspects: NatalAspect[]; houses?: number[] }> = ({
  placements,
  aspects,
  houses,
}) => {
  const center = 310;
  const radius = 220;
  const point = (longitude: number, distance: number) => {
    const angle = ((longitude - 90) * Math.PI) / 180;
    return { x: center + Math.cos(angle) * distance, y: center + Math.sin(angle) * distance };
  };
  const placementByPlanet = new Map(placements.map((placement) => [placement.planet, placement]));
  const houseCusps = houses?.length === 12 ? houses : [];

  return (
    <div className="chart-wheel" aria-label="Printable natal chart wheel">
      <svg viewBox="0 0 620 620" role="img" className="w-full h-auto drop-shadow-2xl">
        <circle cx={center} cy={center} r={radius} className="chart-wheel__ring" />
        <circle cx={center} cy={center} r={radius * 0.82} className="chart-wheel__zodiac" />
        <circle cx={center} cy={center} r={radius * 0.68} className="chart-wheel__inner" />
        {ZODIAC_SIGNS.map((sign, index) => {
          const position = point(index * 30 + 15, radius * 0.91);
          return (
            <g key={sign}>
              <line
                x1={center}
                y1={center - radius}
                x2={center}
                y2={center - radius + 12}
                className="chart-wheel__tick"
                transform={`rotate(${index * 30} ${center} ${center})`}
              />
              <text
                x={position.x}
                y={position.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="chart-wheel__zodiac-label"
              >
                {sign.slice(0, 3)}
              </text>
            </g>
          );
        })}
        {houseCusps.map((cusp, index) => {
          const nextCusp = houseCusps[(index + 1) % houseCusps.length];
          const houseSpan = (nextCusp - cusp + 360) % 360 || 30;
          const midpoint = (cusp + houseSpan / 2) % 360;
          const lineEnd = point(cusp, radius * 0.82);
          const labelPosition = point(midpoint, radius * 0.58);
          return (
            <g key={`house-${index + 1}`}>
              <line
                x1={center}
                y1={center}
                x2={lineEnd.x}
                y2={lineEnd.y}
                className={index === 0 ? "chart-wheel__house chart-wheel__house--first" : "chart-wheel__house"}
              />
              <text
                x={labelPosition.x}
                y={labelPosition.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className={index === 0 ? "chart-wheel__house-label chart-wheel__house-label--first" : "chart-wheel__house-label"}
              >
                {index + 1}
              </text>
            </g>
          );
        })}
        {aspects.slice(0, 24).map((aspect) => {
          const first = placementByPlanet.get(aspect.source);
          const second = placementByPlanet.get(aspect.target);
          if (!first || !second) return null;
          const start = point(first.longitude, radius * 0.62);
          const end = point(second.longitude, radius * 0.62);
          return (
            <line
              key={aspect.source + aspect.target + aspect.aspect_type}
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={aspect.color}
              className="chart-wheel__aspect"
            />
          );
        })}
        {placements.map((placement) => {
          const position = point(placement.longitude, radius * 0.75);
          const labelPosition = point(placement.longitude, radius * 0.69);
          return (
            <g key={placement.planet}>
              <line
                x1={position.x}
                y1={position.y}
                x2={center + (position.x - center) * 0.83}
                y2={center + (position.y - center) * 0.83}
                className="chart-wheel__spoke"
              />
              <circle cx={position.x} cy={position.y} r="7" className="chart-wheel__planet" />
              <text
                x={labelPosition.x}
                y={labelPosition.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="chart-wheel__label"
              >
                {placement.planet.slice(0, 3)}
              </text>
            </g>
          );
        })}
        <text x={center} y={center - 8} textAnchor="middle" className="chart-wheel__title">
          NEX Astro
        </text>
        <text x={center} y={center + 18} textAnchor="middle" className="chart-wheel__subtitle">
          Topocentric Sky Map
        </text>
      </svg>
    </div>
  );
};

const ChartGenerator: React.FC = () => {
  const [birthDate, setBirthDate] = useState("1995-10-24");
  const [birthTime, setBirthTime] = useState("12:00");
  const [birthCity, setBirthCity] = useState("San Francisco, ca");
  const [chart, setChart] = useState<NatalChart | null>(null);
  const [message, setMessage] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [transitReport, setTransitReport] = useState<TransitReport | null>(null);

  const generate = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    setChart(null);
    setTransitReport(null);

    try {
      const response = await fetch(apiUrl("/api/chart/natal"), {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          birth_date: birthDate,
          birth_time: birthTime || null,
          birth_city: birthCity,
        }),
      });
      const payload = (await response.json()) as NatalChart | { detail?: string };
      if (!response.ok) {
        throw new Error(
          "detail" in payload && payload.detail ? payload.detail : "Chart calculation failed."
        );
      }
      const natalChart = payload as NatalChart;
      setChart(natalChart);

      // Fetch accompanying daily transit report
      const transitResponse = await fetch(apiUrl("/api/chart/transits"), {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          birth_date: birthDate,
          birth_time: birthTime || null,
          birth_city: birthCity,
          target_date: new Date().toISOString().slice(0, 10),
        }),
      });
      if (transitResponse.ok) {
        setTransitReport((await transitResponse.json()) as TransitReport);
      }
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : "The chart could not be calculated.");
    } finally {
      setLoading(false);
    }
  };

  const saveChart = async () => {
    if (!chart) return;
    setSaveMessage("");
    try {
      const response = await fetch(apiUrl("/api/charts"), {
        method: "post",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: `${chart.birth_city} Charter Chart`,
          birth_date: chart.birth_date,
          birth_time: chart.birth_time,
          birth_city: chart.birth_city,
        }),
      });
      const payload = (await response.json()) as { detail?: string };
      if (!response.ok) throw new Error(payload.detail || "Sign in to save this chart.");
      setSaveMessage("Successfully archived to your client portal.");
    } catch (error: unknown) {
      setSaveMessage(error instanceof Error ? error.message : "Sign in to save this chart.");
    }
  };

  return (
    <div className={`chart-generator ${chart ? "chart-generator--result" : "chart-generator--form-only"} rounded-3xl border border-emerald-400/25 bg-slate-950/90 p-6 md:p-10 shadow-2xl`}>
      <div className="chart-generator__form-shell">
        {!chart && (
          <div className="chart-generator__empty-state">
            <div className="chart-generator__empty-state-copy">
              <span className="landing-section-kicker">Your private starting point</span>
              <h3>Give your sky a place and a moment</h3>
              <p>
                Enter the local details that anchor your chart. NEX will resolve the coordinates, houses, planetary positions, and active relationships for the Observatory.
              </p>
            </div>
            <div className="chart-generator__empty-state-sequence" aria-label="Chart setup sequence">
              <span><b>01</b><small>Place</small><em>Resolve location</em></span>
              <span><b>02</b><small>Moment</small><em>Set local time</em></span>
              <span><b>03</b><small>Observe</small><em>Open your sky</em></span>
            </div>
          </div>
        )}
        <form onSubmit={generate} className="chart-generator__form" aria-label="Birth chart inputs">
        <label className="text-xs font-mono text-slate-300">
          Exact Birth Date
          <input
            required
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-900 p-3 text-sm text-white focus:border-cyan-400 font-mono"
          />
        </label>
        <label className="text-xs font-mono text-slate-300">
          <span className="birth-time-label">Birth Time <small>(24H Local)</small></span>
          <input
            type="time"
            value={birthTime}
            onChange={(e) => setBirthTime(e.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-900 p-3 text-sm text-white focus:border-cyan-400 font-mono"
          />
        </label>
        <label className="chart-generator__wide text-xs font-mono text-slate-300">
          Birth City or Coordinates
          <input
            required
            value={birthCity}
            onChange={(e) => setBirthCity(e.target.value)}
            placeholder="Ames, Iowa or London, uk"
            className="mt-1 w-full rounded-xl border border-slate-700 bg-slate-900 p-3 text-sm text-white focus:border-cyan-400 font-mono"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="chart-generator__submit landing-button landing-button--primary mt-2 flex items-center justify-center gap-2 rounded-xl bg-cyan-400 py-3 text-sm font-bold text-slate-950 hover:bg-cyan-300 transition"
        >
          {loading ? "calculating ephemeris..." : "calculate topocentric chart"}
          <ArrowRight className="w-4 h-4" />
        </button>
        </form>
      </div>

      {message && <p className="mt-4 text-xs font-mono text-rose-400">{message}</p>}

      {chart && (
        <div className="chart-result mt-8 border-t border-slate-800 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-4">
            <div>
              <span className="text-xs font-bold font-mono text-cyan-400">Resolved Astronomical State</span>
              <h3 className="text-xl font-bold text-white">
                {chart.birth_city} • {chart.birth_date} {chart.birth_time ? `@ ${chart.birth_time}` : ""}
              </h3>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveChart}
                className="landing-button landing-button--outline px-4 py-2 text-xs font-mono"
              >
                Archive to Portal
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="landing-button landing-button--outline px-4 py-2 text-xs font-mono flex items-center gap-1.5"
              >
                <Printer className="w-3.5 h-3.5" /> Print Chart
              </button>
            </div>
          </div>

          {saveMessage && <p className="mt-2 text-xs font-mono text-emerald-300">{saveMessage}</p>}

          <div className="chart-result__visual mt-6">
            <div className="chart-result__wheel-panel">
              <ChartWheel placements={chart.placements} aspects={chart.aspects} houses={chart.angles.cusps} />
            </div>
            <div className="chart-result__below">
              <div className="chart-result__support-column">
                <div className="chart-result__angles grid grid-cols-2 gap-3 text-xs font-mono">
                  <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                    <span className="block text-slate-400">Ascendant</span>
                    <strong className="text-cyan-300">{chart.angles.ascendant?.degree_in_sign ?? "Unavailable"}</strong>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                    <span className="block text-slate-400">Midheaven</span>
                    <strong className="text-amber-300">{chart.angles.midheaven?.degree_in_sign ?? "Unavailable"}</strong>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-xs leading-5 text-slate-400">
                  <p className="font-bold text-cyan-200">Topocentric sky map</p>
                  <p className="mt-2">The wheel shows the resolved planetary geometry. Use the Observatory for the living 3D view and time-based analysis.</p>
                </div>
                {transitReport && (
                  <section className="chart-result__transits rounded-2xl border border-cyan-500/20 bg-slate-900/40 p-5 font-mono">
                    <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                      <span className="text-xs font-bold text-amber-300"><Zap className="mr-1 inline-block h-4 w-4 text-amber-400" />Active Transit Activations</span>
                      <span className="text-[10px] text-slate-400">{transitReport.target_date}</span>
                    </div>
                    <p className="mt-3 text-xs text-slate-300 font-sans">{transitReport.summary}</p>
                    <div className="mt-3 grid grid-cols-1 gap-2 text-[11px]">
                      {transitReport.aspects.slice(0, 6).map((aspect) => (
                        <div key={aspect.headline} className="rounded-lg border border-slate-800 bg-slate-950 p-2.5">
                          <strong style={{ color: aspect.color }}>{aspect.headline}</strong>
                          <span className="ml-1.5 text-slate-500">({aspect.orb.toFixed(2)}° orb)</span>
                          <p className="mt-1 font-sans text-slate-400">{aspect.meaning}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
              <div className="chart-result__data">
                <div className="chart-result__section-heading">
                  <span>Celestial placements</span>
                  <small>{chart.placements.length} bodies resolved</small>
                </div>
                <div className="chart-result__table font-mono">
                  <div className="chart-result__table-head">
                    <span>Celestial body</span>
                    <span>Ecliptic position</span>
                    <span>State</span>
                  </div>
                  {chart.placements.map((placement) => (
                    <div key={placement.planet}>
                      <strong>{placement.planet}</strong>
                      <span>{placement.degree_in_sign}</span>
                      <span className={placement.retrograde ? "is-retrograde" : "text-emerald-300"}>
                        {placement.retrograde ? "Rx Retrograde" : "Direct"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


type OperatingStage = { number: string; label: string; kicker: string; title: string; description: string; icon: React.ComponentType<{ className?: string }>; action: string; route: string };
const OPERATING_STAGES: OperatingStage[] = [
  { number: "01", label: "Observe", kicker: "Free chart engine", title: "Start with the mathematics", description: "Generate bodies, positions, houses, aspects, and angles you can inspect instead of accepting a generic horoscope.", icon: Compass, action: "Generate your chart", route: "#chart" },
  { number: "02", label: "Explore", kicker: "Spatial Observatory", title: "Enter the 3D / 4D Observatory", description: "Rotate through spatial relationships, scrub time, inspect the Orbit menu, and move from a flat chart into a living coordinate system.", icon: Orbit, action: "Open Observatory", route: "/observatory" },
  { number: "03", label: "Model", kicker: "Personas + entities", title: "Place your world inside it", description: "Add personas, companies, projects, deals, and life events so decisions are viewed alongside the people and timelines that shape them.", icon: Users, action: "Open your portal", route: "/portal" },
  { number: "04", label: "Operate", kicker: "Tools + workflows", title: "Turn tools into workflows", description: "Chain review, comparison, calendar context, protocols, snapshots, and the next action into a repeatable operating process.", icon: Zap, action: "Explore workflows", route: "/observatory" },
  { number: "05", label: "Record", kicker: "Snapshots + calendar", title: "Make time visible", description: "Build Snapshot Series timelines, mark life events, intersect calendar context, and keep a private record of what changes.", icon: Calendar, action: "View snapshots", route: "/observatory" },
  { number: "06", label: "Share", kicker: "NEX ID + alliances", title: "Form alliances around the work", description: "Choose a unique NEX ID, create an alliance, publish useful workflows, and invite the people who matter.", icon: Share2, action: "Choose your NEX ID", route: "/portal" },
];

const OperatingSystemSection: React.FC = () => {
  const [activeStage, setActiveStage] = useState(0);
  const stage = OPERATING_STAGES[activeStage];
  const StageIcon = stage.icon;
  const openStage = () => stage.route.startsWith("#") ? document.getElementById(stage.route.slice(1))?.scrollIntoView({ behavior: "smooth" }) : window.location.assign(stage.route);
  return <section id="operating-system" className="landing-operating-system landing-reveal-section"><div className="landing-operating-system__intro"><span className="landing-section-kicker">[ The NEX operating system ]</span><h2>From raw coordinates to a life you can navigate</h2><p>NEX is not six disconnected features. It is one operating loop: observe the system, place your world inside it, act with tools, record what changes, and coordinate with the people who matter.</p></div><div className="landing-operating-tabs" role="tablist">{OPERATING_STAGES.map((item,index) => { const Icon=item.icon; return <button key={item.number} type="button" role="tab" aria-selected={index===activeStage} className={index===activeStage ? "landing-operating-tab is-active" : "landing-operating-tab"} onClick={()=>setActiveStage(index)}><span>{item.number}</span><Icon/><strong>{item.label}</strong></button>; })}</div><div className="landing-operating-stage"><div className="landing-operating-stage__visual"><div className="landing-stage-orbit"><span className="landing-stage-orbit__core"><StageIcon/><b>{stage.number}</b><small>{stage.kicker}</small></span><i/><i/><i/></div></div><div className="landing-operating-stage__copy"><span className="landing-section-kicker">Stage {stage.number} / {stage.kicker}</span><h3>{stage.title}</h3><p>{stage.description}</p><span className="landing-stage-sequence">Input → Observation → Action → Record</span><button type="button" onClick={openStage} className="landing-button landing-button--primary">{stage.action}<ArrowRight/></button></div></div><div className="landing-workflow-strip"><strong>One decision. Multiple instruments. One saved record.</strong><span>Review active pressure</span><span>Compare entities</span><span>Intersect calendar</span><span>Run strategic tool</span><span>Save snapshot</span></div></section>;
};

const SnapshotStorySection: React.FC = () => <section id="snapshots" className="landing-snapshot-story landing-reveal-section"><div className="landing-snapshot-story__intro"><span className="landing-section-kicker">[ The celestial snapshot series ]</span><h2>Check the sky against the moments that matter</h2><p>Start with your birth chart, then add dated snapshots, life events, and optional calendar context to compare what was happening in the sky with what was happening in your life.</p></div><div className="landing-snapshot-steps">{[{icon:Layers,title:"Capture the baseline",text:"Anchor the series to your birth data, location, and chosen time horizon."},{icon:Orbit,title:"Track the moving sky",text:"Save structured celestial states as planets, aspects, and geometry develop."},{icon:Calendar,title:"Mark what mattered",text:"Place career, relationship, financial, health, and personal milestones on the timeline."},{icon:TrendingUp,title:"Calibrate the signal",text:"Compare enough marked events against the record before a recurring shape earns attention."}].map((item,index)=>{const Icon=item.icon;return <article key={item.title}><span>0{index+1}</span><Icon/><h3>{item.title}</h3><p>{item.text}</p></article>;})}</div><div className="landing-snapshot-orb"><span>Track the pattern</span><i/><i/><i/></div><div className="landing-snapshot-plans"><article><span>Start from</span><h3>10 day explorer series</h3><p>Experience a complete moving-sky timeline, inspect changing aspects, and learn what a personal pattern window feels like.</p><strong>No payment required</strong></article><article><span>Go deeper</span><h3>20 and 30 day observer series</h3><p>Follow developing patterns, add life events, and compare the timeline against a meaningful period.</p><strong>Designed for paid access</strong></article><article><span>Build a record</span><h3>60 to 365 day research series</h3><p>Build historical baselines, recurring configurations, annual reviews, and deeper personal analysis.</p><strong>Longer durations belong in membership</strong></article></div><div className="landing-calendar-choices"><article><h3>Connected Calendar</h3><strong>Bring the schedule you already use</strong><p>Optionally intersect selected Google Calendar context around meetings, launches, deadlines, and reviews.</p></article><article><h3>Private NEX Calendar</h3><strong>Keep your timeline inside NEX</strong><p>Create a self-contained calendar for life events, checkpoints, and follow-up actions.</p></article></div></section>;

const ParticipationSection: React.FC = () => <section id="participation" className="landing-participation landing-participation--canonical landing-reveal-section"><div className="landing-participation__intro"><span className="landing-section-kicker">NEX participation economy</span><h2>Build something useful<br/><em>share in its momentum</em></h2><p>The network rewards the people who bring aligned observers, create useful instruments, and help valuable workflows travel.</p></div><div className="landing-participation-loop"><span>The value loop</span><small>Invite / Build / Share</small><div>{[{title:"Invite aligned people",text:"Share your NEX ID and bring people into an observatory built for action, not passive consumption."},{title:"Build useful instruments",text:"Turn your methods into tools and workflows that other observers can run, adapt, and improve."},{title:"Share in qualifying value",text:"Referral and creator revenue follow published eligibility, attribution, and payout terms."}].map((item,index)=><article key={item.title}><b>0{index+1}</b><h3>{item.title}</h3><p>{item.text}</p><ArrowRight/></article>)}</div></div><button type="button" onClick={()=>window.location.assign("/referrals")} className="landing-text-button">Open referral dashboard<ArrowRight/></button></section>;

export const LandingPage: React.FC = () => {
  const landingRef = useRef<HTMLElement | null>(null);
  const [telemetry, setTelemetry] = useState<TelemetryStripData>({
    frictionScore: 52,
    aspectCount: 14,
    tightestAspect: "venus square pluto",
    tightestOrb: "0.11° orb",
    tacticalDirective: "freeze unhedged capital allocations for 24 hours",
  });

  useEffect(() => {
    // Dynamic telemetry hydration from active transit engine
    fetch(apiUrl("/api/chart/transits"), {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        birth_date: "2000-01-01",
        birth_time: "12:00",
        birth_city: "San Francisco, ca",
        target_date: new Date().toISOString().slice(0, 10),
      }),
    })
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json();
        const aspects = data.aspects || [];
        const count = aspects.length;
        const score = Math.min(95, Math.max(20, Math.round(100 - count * 3.8)));
        const tightest = aspects[0] || {
          source: "Venus",
          target: "Pluto",
          aspect_type: "square",
          orb: 0.11,
        };
        setTelemetry({
          frictionScore: score,
          aspectCount: count,
          tightestAspect: `${tightest.source} ${tightest.aspect_type} ${tightest.target}`.toLowerCase(),
          tightestOrb: `${tightest.orb.toFixed(2)}° orb`,
          tacticalDirective:
            score < 65
              ? "Maintain conservative liquidity hedges and audit partnership contracts"
              : "Execute aggressive strategic expansion across harmonic corridors",
        });
      })
      .catch(() => {
        // Retain verified fallback telemetry
      });
  }, []);

  useEffect(() => {
    const landingRoot = landingRef.current;
    if (!landingRoot) return;

    const revealTargets = Array.from(landingRoot.querySelectorAll<HTMLElement>(".landing-reveal-section"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          gsap.fromTo(
            entry.target,
            { y: 38 },
            { y: 0, duration: 0.8, ease: "power2.out" },
          );
          observer.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: "0px 0px -12% 0px", threshold: 0.08 },
    );

    revealTargets.forEach((target) => observer.observe(target));
    gsap.fromTo(
      ".landing-hero-content",
      { autoAlpha: 0, y: 24 },
      { autoAlpha: 1, y: 0, duration: 0.9, ease: "power3.out", stagger: 0.1 },
    );

    return () => observer.disconnect();
  }, []);

  const openObservatory = () => window.location.assign("/observatory");
  const openPortal = () => window.location.assign("/portal");
  const openRoute = (route: string) => window.location.assign(route);

  return (
    <main ref={landingRef} className="landing-page landing-page--master bg-[#010713] text-slate-100 selection:bg-cyan-500 selection:text-slate-950 font-sans">
      <LandingGuidePanel />
      <div className="landing-gradient" aria-hidden="true" />
      <nav className="landing-nav landing-nav--redesigned sticky top-0 z-50 flex items-center justify-between border-b border-cyan-500/15 bg-slate-950/80 px-6 py-4 backdrop-blur-xl">
        <a href="/" className="landing-brand flex items-center gap-2 font-bold font-serif text-lg tracking-wider text-white"><CircleDot className="h-5 w-5 text-cyan-400" /><span>NEX Astro</span></a>
        <div className="landing-nav__links flex items-center gap-5 text-[10px] font-mono text-slate-300">
          <a href="#operating-system" className="transition hover:text-cyan-300">How NEX Works</a><a href="#capabilities" className="transition hover:text-cyan-300">Capabilities</a><a href="#commons" className="transition hover:text-cyan-300">Tools & Workflows</a><a href="#snapshots" className="transition hover:text-cyan-300">Snapshots</a><a href="#participation" className="transition hover:text-cyan-300">Profit Sharing</a><a href="#pricing" className="transition hover:text-cyan-300">Pricing</a><a href="/readings" className="transition hover:text-cyan-300">Free readings</a><button type="button" onClick={openPortal} className="transition hover:text-cyan-300">Portal</button><button type="button" onClick={openObservatory} className="rounded-xl bg-cyan-400 px-4 py-2 font-bold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-300">Open Observatory <ArrowRight className="ml-1 inline h-3.5 w-3.5" /></button>
        </div>
      </nav>
      <section className="landing-reveal-section landing-hero-section relative mx-auto flex max-w-6xl flex-col items-center px-6 pb-16 pt-20 text-center"><div className="landing-hero-content mb-8 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-950/40 px-3 py-1 text-[11px] font-mono text-cyan-300"><Activity className="h-3.5 w-3.5 text-emerald-400" /> Live Celestial Telemetry • High-Precision 4D Spatial Computing</div><h1 className="landing-hero-content landing-hero-title"><span className="shimmer-text shimmer-text--headline">Stop Reading Generic Horoscopes</span><span className="shimmer-text shimmer-text--accent">See the Forces Shaping Your Next Move</span></h1><p className="landing-hero-content mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">NEX Astro turns precise celestial mathematics, people, decisions, and life events into a navigable observatory for time. Start with the math for free, then add your world when you are ready.</p><div className="landing-hero-content landing-hero-actions mt-8 flex flex-col gap-4 sm:flex-row"><button type="button" onClick={openObservatory} className="flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-8 py-4 text-sm font-bold text-slate-950 shadow-xl shadow-cyan-400/20 transition hover:bg-cyan-300">Enter the Observatory <ArrowRight className="h-4 w-4" /></button><button type="button" onClick={() => document.getElementById("chart")?.scrollIntoView({ behavior: "smooth" })} className="rounded-xl border border-slate-700 bg-slate-900/60 px-8 py-4 text-sm font-bold text-white transition hover:border-cyan-400/50">Generate Free 2D Chart</button></div><div className="landing-hero-content landing-telemetry-panel mt-12 w-full max-w-4xl rounded-2xl border border-cyan-500/30 bg-slate-950/80 p-4 text-left font-mono shadow-2xl backdrop-blur-md"><div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-2 text-[11px]"><span className="flex items-center gap-1.5 font-bold text-cyan-400"><Activity className="h-4 w-4 text-emerald-400" /> Live Celestial Feed</span><span className="text-slate-500">{new Date().toUTCString()}</span></div><div className="landing-telemetry-grid mt-3 grid grid-cols-1 gap-3 text-xs md:grid-cols-3"><div className="landing-telemetry-pressure"><div className="landing-telemetry-score" aria-label={`Current pressure index ${telemetry.frictionScore} out of 100`}><strong>{telemetry.frictionScore}</strong><span>/100</span></div><div className="landing-telemetry-copy"><span className="block text-[10px] text-slate-400">Current Pressure Index</span><strong className="font-bold text-amber-300">{telemetry.frictionScore < 65 ? "COMPRESSION" : "FLOW"}</strong><small>Live pressure across the active celestial field</small></div></div><div className="landing-telemetry-metric"><span className="block text-[10px] text-slate-400">Active Aspect Vectors</span><strong className="font-bold text-cyan-300">{telemetry.aspectCount}</strong><small>Ray-cast connections</small></div><div className="landing-telemetry-metric"><span className="block text-[10px] text-slate-400">Tightest Active Orb</span><strong className="font-bold text-rose-400">{telemetry.tightestOrb}</strong><small>{telemetry.tightestAspect}</small></div></div><div className="mt-3 flex flex-wrap items-start gap-2 border-t border-slate-800/80 pt-2.5 text-xs text-slate-300"><span className="text-[10px] font-bold uppercase text-cyan-400">Tactical Recommendation:</span><span>{telemetry.tacticalDirective}</span></div></div></section>
      <OperatingSystemSection />
      <section id="manifesto" className="landing-reveal-section landing-manifesto-section border-t border-slate-800/80 bg-slate-950/40 px-6 py-20"><div className="landing-manifesto__inner mx-auto text-center"><div className="landing-manifesto__header"><span className="landing-section-kicker">[ The Manifesto ]</span><h2 className="text-3xl font-bold font-serif text-white sm:text-5xl">Beyond Generic Astrology</h2><p className="landing-manifesto__lead text-base text-slate-300 sm:text-lg">Most modern astrology apps are designed for passive consumption. They rely on vague, generalized statements that could apply to anyone on any given day. When high-stakes founders, operators, and clear-headed thinkers look at these apps, they are left with one conclusion: <em>This is meaningless</em></p></div><div className="landing-manifesto__statement"><span className="landing-section-kicker">NEX Astro is built on a different premise</span><p className="landing-manifesto__statement-copy text-base text-slate-300">Astrology is not a fortune-telling gimmick. At its mathematical core, it is a <strong>high-dimensional coordinate system of cyclic time, angular harmonics, and human psychological tension</strong></p></div><div className="landing-manifesto__cards"><article className="landing-manifesto__card"><span className="landing-manifesto__index">01</span><h4>Clear signals</h4><p>No vague platitudes. See the exact relationship between your timing, active planetary patterns, and the decisions in front of you.</p></article><article className="landing-manifesto__card"><span className="landing-manifesto__index">02</span><h4>Useful context</h4><p>No mystical fatalism. Understand pressure as a changing condition and receive practical guidance for navigating it with intention.</p></article><article className="landing-manifesto__card"><span className="landing-manifesto__index">03</span><h4>A living view</h4><p>No flat paper wheel. Explore an interactive spatial view that keeps your relationships, timing, and celestial context connected.</p></article></div></div></section>
      <ReceiptsSection />
      <CapabilitiesSection />
      <CommonsSection />
      <DataProtectionSection />
      <section id="chart" className="landing-reveal-section border-t border-slate-800/80 bg-slate-950/60 px-6 py-20"><div className="mx-auto max-w-5xl"><div className="mb-12 text-center"><span className="landing-section-kicker">[ The Accessible Beginning ]</span><h2 className="mt-2 text-3xl font-bold font-serif text-white sm:text-5xl">Your Birth Chart, Brought to Life</h2><p className="chart-generator__sequence mt-4 text-sm text-slate-300">1. Enter Coordinates → 2. Launch the Observatory → 3. Add Entities → 4. Check the Friction Index</p></div><ChartGenerator/></div></section>
      <SnapshotStorySection /><NexIdentitySection /><ParticipationSection />
      <section id="pricing" className="landing-reveal-section border-t border-slate-800/80 px-6 py-20"><div className="mx-auto max-w-6xl"><div className="mb-16 text-center"><span className="landing-section-kicker">[ Capital Ladder ]</span><h2 className="mt-2 text-3xl font-bold font-serif text-white sm:text-5xl">Simple, Predictable Access</h2><p className="mt-4 text-sm text-slate-400">Clear pricing, direct secure checkout, and no hidden access requirements.</p></div><div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{PRICING_TIERS.map((tier)=><article key={tier.name} className={tier.highlight ? "landing-price-card is-featured" : "landing-price-card"}><div><div className="flex items-center justify-between gap-2"><h3>{tier.name}</h3>{tier.badge&&<span>{tier.badge}</span>}</div><div className="my-3 flex items-baseline gap-1"><strong>{tier.price}</strong><small>{tier.cadence}</small></div><p>{tier.desc}</p><ul>{tier.features.map((feature)=><li key={feature}><Check/>{feature}</li>)}</ul></div><button type="button" onClick={openObservatory}>{tier.cta}</button></article>)}</div></div></section>
      <FinalLandingSection />
    </main>
  );

};